import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertOctagon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Panel } from '@/components/ui/Panel';

interface Props {
  children: ReactNode;
  /** Changing this value remounts the boundary — used to reset on navigation. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

/**
 * Route-level boundary. A render crash on one screen must not take down the
 * shell: the trader keeps their navigation and can move to Traffic, which is
 * the screen that actually costs money to lose.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(previous: Props): void {
    if (previous.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Replace with the platform's error reporter when one is wired up.
    // eslint-disable-next-line no-console
    console.error('Route render failed', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <Panel padding="loose" className="max-w-prose">
        <div className="flex items-start gap-3">
          <AlertOctagon
            size={18}
            strokeWidth={1.5}
            aria-hidden="true"
            className="mt-0.5 shrink-0 text-danger-500"
          />
          <div>
            <h1 className="text-md font-semibold tracking-tight text-fg">
              Не удалось отобразить экран
            </h1>
            <p className="mt-1.5 text-sm leading-normal text-fg-secondary">
              Ничего не было отправлено, статусы платежей не изменились. Обновите экран или
              перейдите в «Трафик», если вас ждёт платёж.
            </p>
            <p className="num mt-3 rounded-sm border border-line bg-sunken px-2 py-1.5 text-xs text-fg-secondary">
              {error.message}
            </p>
            <div className="mt-4 flex gap-2">
              <Button variant="primary" onClick={() => this.setState({ error: null })}>
                Попробовать снова
              </Button>
              <Button onClick={() => window.location.reload()}>Обновить страницу</Button>
            </div>
          </div>
        </div>
      </Panel>
    );
  }
}
